export interface ViewLoader {
    whileLoading<T>(
        f: (() => Promise<T> | T) | Promise<T>,
        loaderKey: string,
        message?: string,
        coverWholePage?: boolean
    ): Promise<T>
    setLoading(loading: React.SetStateAction<boolean>): void
    setLoadMessage(message: React.SetStateAction<string>): void
    setOverWholePage(coverWholePage: React.SetStateAction<boolean>): void
    pendingPromises: Map<string, number>
    lastAddedKey: string
}

export interface ViewLoaderProxy {
    whileLoading<T>(f: (() => Promise<T> | T) | Promise<T>, message?: string, coverWholePage?: boolean): Promise<T>
    setLoading(loading: React.SetStateAction<boolean>): void
    setLoadMessage(message: React.SetStateAction<string>): void
    setOverWholePage(coverWholePage: React.SetStateAction<boolean>): void
    pendingPromises: number
}

// singleton instance to handle multiple useViewLoader calls
let loaderObjectInstance: ViewLoader | null = null

function incrementPendingPromisesForKey(pendingPromises: Map<string, number>, key: string): void {
    pendingPromises.set(key, (pendingPromises.get(key) || 0) + 1)
}

function decrementPendingPromisesForKey(pendingPromises: Map<string, number>, key: string): void {
    pendingPromises.set(key, (pendingPromises.get(key) || 0) - 1)
}

function hideLoaderWhenNoPromisesPending(
    pendingPromises: Map<string, number>,
    loaderKey: string,
    setVisible: (_: React.SetStateAction<boolean>) => void,
    lastAddedKey: string
): void {
    if (pendingPromises.get(loaderKey) === 0 && lastAddedKey === loaderKey) {
        setVisible(false)
    }
}

class LoaderObjectProxy implements ViewLoaderProxy {
    private loaderObjectInstance: ViewLoader
    private loaderKey: string

    constructor(loaderObjectInstance: ViewLoader, loaderKey: string) {
        this.loaderObjectInstance = loaderObjectInstance
        this.loaderKey = loaderKey

        this.whileLoading = this.whileLoading.bind(this)
        this.setOverWholePage = this.setOverWholePage.bind(this)
        this.setLoading = this.setLoading.bind(this)
        this.setLoadMessage = this.setLoadMessage.bind(this)
    }

    public get pendingPromises(): number {
        return this.loaderObjectInstance.pendingPromises.get(this.loaderKey) || 0
    }

    public whileLoading<T>(f: (() => Promise<T> | T) | Promise<T>, message?: string, coverWholePage?: boolean) {
        return this.loaderObjectInstance.whileLoading(f, this.loaderKey, message, coverWholePage)
    }

    public setOverWholePage(coverWholePage: React.SetStateAction<boolean>): void {
        this.loaderObjectInstance.setOverWholePage(coverWholePage)
    }

    public setLoading(isLoading: React.SetStateAction<boolean>): void {
        this.loaderObjectInstance.setLoading(isLoading)
    }

    public setLoadMessage(message: React.SetStateAction<string>): void {
        this.loaderObjectInstance.setLoadMessage(message)
    }
}

export function getLoaderObjectSingletonInstance(
    setVisible: (loading: React.SetStateAction<boolean>) => void,
    setLoadMessage: (message: React.SetStateAction<string>) => void,
    setOverWholePage: (coverWholePage: React.SetStateAction<boolean>) => void,
    loaderKey = 'default'
): ViewLoaderProxy {
    if (!loaderObjectInstance) {
        loaderObjectInstance = {
            lastAddedKey: '',
            pendingPromises: new Map<string, number>(),
            async whileLoading<T>(
                f: (() => Promise<T> | T) | Promise<T>,
                loaderKey: string,
                message?: string,
                coverWholePage?: boolean
            ) {
                let oldCoverWholePage: boolean | undefined = undefined
                if (typeof coverWholePage !== 'undefined') {
                    setOverWholePage(old => {
                        oldCoverWholePage = old
                        return coverWholePage
                    })
                }

                try {
                    this.lastAddedKey = loaderKey
                    setVisible(true)
                    incrementPendingPromisesForKey(this.pendingPromises, loaderKey)
                    if (typeof message !== 'undefined') {
                        setLoadMessage(message)
                    }

                    const ret = typeof f === 'function' ? f() : f

                    return await ret
                } finally {
                    decrementPendingPromisesForKey(this.pendingPromises, loaderKey)
                    hideLoaderWhenNoPromisesPending(this.pendingPromises, loaderKey, setVisible, this.lastAddedKey)

                    setLoadMessage('')
                    if (typeof oldCoverWholePage !== 'undefined') {
                        setOverWholePage(oldCoverWholePage)
                    }
                }
            },
            setLoading: setVisible,
            setLoadMessage: setLoadMessage,
            setOverWholePage: setOverWholePage
        }
    }

    return new LoaderObjectProxy(loaderObjectInstance, loaderKey)
}
